import React, { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Minus, Search, AlertTriangle, Package } from "lucide-react";
import { toast } from "sonner";

const THREAD_TYPES = ["Rayon", "Polyester", "Metallic", "Cotton"];

export default function Inventory() {
  const [threads, setThreads] = useState([]);
  const [consumables, setConsumables] = useState([]);
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState("thread");
  const [tForm, setTForm] = useState({ code: "", name: "", brand: "", type: "Rayon", stock: 0, min_stock: 0, unit: "cones" });
  const [cForm, setCForm] = useState({ name: "", category: "", stock: 0, min_stock: 0, unit: "pcs" });

  const load = useCallback(() => {
    api.get("/threads").then((r) => setThreads(r.data)).catch(() => {});
    api.get("/consumables").then((r) => setConsumables(r.data)).catch(() => {});
    api.get("/stock-logs").then((r) => setLogs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const adjust = async (type, id, delta) => {
    try { await api.post(`/${type}s/${id}/adjust`, { delta, note: delta > 0 ? "Stok masuk" : "Stok keluar" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const saveNew = async () => {
    try {
      if (addType === "thread") { await api.post("/threads", { ...tForm, stock: +tForm.stock, min_stock: +tForm.min_stock }); }
      else { await api.post("/consumables", { ...cForm, stock: +cForm.stock, min_stock: +cForm.min_stock }); }
      toast.success("Item ditambahkan"); setAddOpen(false); load();
      setTForm({ code: "", name: "", brand: "", type: "Rayon", stock: 0, min_stock: 0, unit: "cones" });
      setCForm({ name: "", category: "", stock: 0, min_stock: 0, unit: "pcs" });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const fThreads = threads.filter((t) => [t.code, t.name, t.brand].join(" ").toLowerCase().includes(q.toLowerCase()));

  const StockCell = ({ item, type }) => {
    const low = item.stock <= item.min_stock;
    return (
      <div className="flex items-center gap-2">
        <Button data-testid={`dec-${item.id}`} size="icon" variant="outline" className="h-7 w-7" onClick={() => adjust(type, item.id, -1)}><Minus className="h-3 w-3" /></Button>
        <span className={`w-16 text-center font-mono text-sm font-bold ${low ? "text-rose-600" : "text-slate-800"}`}>{item.stock} {item.unit}</span>
        <Button data-testid={`inc-${item.id}`} size="icon" variant="outline" className="h-7 w-7" onClick={() => adjust(type, item.id, 1)}><Plus className="h-3 w-3" /></Button>
        {low && <span data-testid="badge-low-stock-alert" className="flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 px-2 py-0.5 text-[10px] font-bold text-rose-700"><AlertTriangle className="h-3 w-3" /> Menipis</span>}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">Stok Benang & Bahan</h1>
          <p className="text-sm text-slate-500">Pantau stok benang, konsumabel, dan riwayat keluar-masuk.</p>
        </div>
        <Button data-testid="btn-add-stock" className="bg-blue-600 hover:bg-blue-700" onClick={() => setAddOpen(true)}><Plus className="mr-2 h-4 w-4" /> Tambah Item</Button>
      </div>

      <Tabs defaultValue="threads">
        <TabsList>
          <TabsTrigger value="threads" data-testid="tab-threads">Benang</TabsTrigger>
          <TabsTrigger value="consumables" data-testid="tab-consumables">Konsumabel</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Riwayat Stok</TabsTrigger>
        </TabsList>

        <TabsContent value="threads">
          <Card className="border-slate-200/80 shadow-sm">
            <div className="p-4 border-b border-slate-100">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input data-testid="input-thread-search" className="pl-9" placeholder="Cari kode / warna benang..." value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50/50">
                  <TableHead>Kode</TableHead><TableHead>Warna / Nama</TableHead><TableHead>Brand</TableHead><TableHead>Tipe</TableHead><TableHead>Stok</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {fThreads.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="font-mono text-xs text-blue-600">{t.code}</TableCell>
                      <TableCell className="font-semibold text-slate-800">{t.name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{t.brand}</TableCell>
                      <TableCell className="text-sm text-slate-600">{t.type}</TableCell>
                      <TableCell><StockCell item={t} type="thread" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="consumables">
          <Card className="border-slate-200/80 shadow-sm overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50/50">
                <TableHead>Nama</TableHead><TableHead>Kategori</TableHead><TableHead>Stok</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {consumables.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-semibold text-slate-800">{c.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{c.category}</TableCell>
                    <TableCell><StockCell item={c} type="consumable" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="logs">
          <Card className="border-slate-200/80 shadow-sm overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50/50">
                <TableHead>Waktu</TableHead><TableHead>Item</TableHead><TableHead>Perubahan</TableHead><TableHead>Catatan</TableHead><TableHead>Oleh</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {logs.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="text-sm text-slate-600">{formatDate(l.created_at, true)}</TableCell>
                    <TableCell className="text-sm font-medium text-slate-800">{l.item_name}</TableCell>
                    <TableCell className={`font-mono text-sm font-bold ${l.delta > 0 ? "text-emerald-600" : "text-rose-600"}`}>{l.delta > 0 ? "+" : ""}{l.delta}</TableCell>
                    <TableCell className="text-sm text-slate-500">{l.note}</TableCell>
                    <TableCell className="text-sm text-slate-500">{l.by}</TableCell>
                  </TableRow>
                ))}
                {logs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Belum ada riwayat.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Tambah Item Stok</DialogTitle></DialogHeader>
          <Tabs value={addType} onValueChange={setAddType}>
            <TabsList className="w-full"><TabsTrigger value="thread" className="flex-1">Benang</TabsTrigger><TabsTrigger value="consumable" className="flex-1">Konsumabel</TabsTrigger></TabsList>
            <TabsContent value="thread" className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Kode</Label><Input value={tForm.code} onChange={(e) => setTForm({ ...tForm, code: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Brand</Label><Input value={tForm.brand} onChange={(e) => setTForm({ ...tForm, brand: e.target.value })} /></div>
                <div className="space-y-1.5 col-span-2"><Label>Warna / Nama</Label><Input value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Tipe</Label>
                  <Select value={tForm.type} onValueChange={(v) => setTForm({ ...tForm, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{THREAD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label>Satuan</Label><Input value={tForm.unit} onChange={(e) => setTForm({ ...tForm, unit: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Stok</Label><Input type="number" value={tForm.stock} onChange={(e) => setTForm({ ...tForm, stock: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Stok Minimal</Label><Input type="number" value={tForm.min_stock} onChange={(e) => setTForm({ ...tForm, min_stock: e.target.value })} /></div>
              </div>
            </TabsContent>
            <TabsContent value="consumable" className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2"><Label>Nama</Label><Input value={cForm.name} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Kategori</Label><Input value={cForm.category} onChange={(e) => setCForm({ ...cForm, category: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Satuan</Label><Input value={cForm.unit} onChange={(e) => setCForm({ ...cForm, unit: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Stok</Label><Input type="number" value={cForm.stock} onChange={(e) => setCForm({ ...cForm, stock: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Stok Minimal</Label><Input type="number" value={cForm.min_stock} onChange={(e) => setCForm({ ...cForm, min_stock: e.target.value })} /></div>
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Batal</Button>
            <Button data-testid="btn-save-stock" className="bg-blue-600 hover:bg-blue-700" onClick={saveNew}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
